import React, { useState } from 'react';
import useAutoSave from '../../hooks/useAutoSave';
import AutoSaveStatus from '../Global/AutoSaveStatus';
import './scss/FeatureSettings.scss';
import CircleCheckOutlined from '../icons/CircleCheckOutlined';
import Close from '../icons/Close';

const FeatureSettings = ({ config, onUpdate, loading }) => {
  const [features, setFeatures] = useState(config?.features || {});
  const [domainWhitelist, setDomainWhitelist] = useState(config?.features?.domainWhitelist || []);
  const [newDomain, setNewDomain] = useState('');

  // Auto-save hook
  const { autoSaving, lastSaved, autoSave } = useAutoSave((data) => onUpdate(data));

  const handleFeatureChange = (feature, value) => {
    const updatedFeatures = {
      ...features,
      [feature]: value
    };
    setFeatures(updatedFeatures);

    // Auto-save the changes
    const updatedData = {
      features: {
        ...updatedFeatures,
        domainWhitelist: domainWhitelist
      }
    };
    autoSave(updatedData);
  };

  const addDomain = () => {
    if (newDomain && !domainWhitelist.includes(newDomain)) {
      const updatedDomainList = [...domainWhitelist, newDomain];
      setDomainWhitelist(updatedDomainList);
      setNewDomain('');

      // Auto-save the changes
      const updatedData = {
        features: {
          ...features,
          domainWhitelist: updatedDomainList
        }
      };
      autoSave(updatedData);
    }
  };

  const removeDomain = (domain) => {
    const updatedDomainList = domainWhitelist.filter(d => d !== domain);
    setDomainWhitelist(updatedDomainList);

    // Auto-save the changes
    const updatedData = {
      features: {
        ...features,
        domainWhitelist: updatedDomainList
      }
    };
    autoSave(updatedData);
  };

  const isValidDomain = (domain) => {
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    return domainRegex.test(domain);
  };

  const featureList = [
    {
      key: 'allowPublicSignup',
      title: 'Allow Public Signup',
      description: 'Allow anyone to create an account from your custom domain',
      icon: 'person_add',
      warning: 'When enabled, anyone can sign up using your custom domain.'
    },
    {
      key: 'customBranding',
      title: 'Custom Branding',
      description: 'Enable custom logos, colors, and app name',
      icon: 'palette',
      disabled: true,
      note: 'This feature is always enabled for white label instances'
    },
    {
      key: 'customDomain',
      title: 'Custom Domain',
      description: 'Use your own domain instead of subdomain.zoobbe.com',
      icon: 'language',
      disabled: true,
      note: 'This feature is always enabled for white label instances'
    },
    {
      key: 'analytics',
      title: 'Analytics Dashboard',
      description: 'Access to detailed usage analytics and insights',
      icon: 'analytics',
      disabled: true,
      note: 'This feature is always enabled for white label instances'
    },
    {
      key: 'apiAccess',
      title: 'API Access',
      description: 'Enable REST API access for integrations',
      icon: 'api',
      comingSoon: true
    },
    {
      key: 'ssoIntegration',
      title: 'SSO Integration',
      description: 'Single Sign-On with SAML/OAuth providers',
      icon: 'security',
      comingSoon: true
    },
    {
      key: 'customEmailDomain',
      title: 'Custom Email Domain',
      description: 'Send emails from your own domain',
      icon: 'palette',
      disabled: true,
      note: 'This feature is always enabled for white label instances'
    },
    {
      key: 'whitelistDomains',
      title: 'Domain Whitelist',
      description: 'Restrict signups to specific email domains',
      icon: 'domain_verification',
      warning: 'When enabled, only users with email addresses from whitelisted domains can sign up'
    },
    {
      key: 'customOnboarding',
      title: 'Custom Onboarding',
      description: 'Create custom onboarding flows for new users',
      icon: 'school',
      comingSoon: true
    },
    {
      key: 'advancedPermissions',
      title: 'Advanced Permissions',
      description: 'Granular role-based access control',
      icon: 'admin_panel_settings',
      comingSoon: true
    }
  ];

  return (
    <div className="feature-settings">
      <div className="section">
        <h3>Feature Configuration</h3>
        <p>Control which features are available in your white label instance.</p>
      </div>

      <div className="features-list">
        {featureList.map((feature) => (
          <div
            key={feature.key}
            className={`feature-item${feature.comingSoon ? ' coming-soon' : ''}`}
          >
            <div className="feature-icon">
              <span className="material-symbols-outlined">{feature.icon}</span>
            </div>

            <div className="feature-content">
              <div className="feature-header">
                <h4>{feature.title}</h4>
                {feature.comingSoon && (
                  <span className="badge coming-soon">Coming Soon</span>
                )}
                {feature.disabled && (
                  <span className="badge always-on">Always On</span>
                )}
              </div>

              <p className="feature-description">{feature.description}</p>

              {feature.warning && features[feature.key] && (
                <div className="feature-warning">
                  <span className="material-symbols-outlined">warning</span>
                  {feature.warning}
                </div>
              )}

              {feature.note && (
                <div className="feature-note">
                  <span className="material-symbols-outlined">info</span>
                  {feature.note}
                </div>
              )}
            </div>

            <div className="feature-toggle">
              {!feature.disabled && !feature.comingSoon ? (
                <label className="toggle-switch">
                  <input
                    disabled={feature.disabled}
                    type="checkbox"
                    checked={features[feature.key] || false}
                    onChange={(e) => handleFeatureChange(feature.key, e.target.checked)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              ) : (
                <div className="toggle-placeholder">
                  {feature.disabled && (
                    <CircleCheckOutlined size={20} color="#22c55e" />
                  )}
                  {feature.comingSoon && (
                    <span className="material-symbols-outlined">schedule</span>
                  )}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {features.whitelistDomains && (
        <div className="section">
          <h3>Domain Whitelist</h3>
          <p className="section-description">
            Restrict signups to specific email domains. Only users with email addresses from these domains will be able to create accounts.
          </p>

          <div className="domain-whitelist">
            <div className="add-domain">
              <div className="domain-input-group">
                <input
                  type="text"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  placeholder="example.com"
                  className={newDomain && !isValidDomain(newDomain) ? 'error' : ''}
                />
                <button
                  onClick={addDomain}
                  disabled={!newDomain || !isValidDomain(newDomain) || domainWhitelist.includes(newDomain)}
                  className="btn btn-secondary"
                >
                  Add Domain
                </button>
              </div>
              {newDomain && !isValidDomain(newDomain) && (
                <span className="error-text">Please enter a valid domain (e.g., example.com)</span>
              )}
            </div>

            {domainWhitelist.length > 0 && (
              <div className="domain-list">
                <h4>Whitelisted Domains</h4>
                <div className="domain-tags">
                  {domainWhitelist.map((domain, index) => (
                    <div key={index} className="domain-tag">
                      <span className="domain-name">{domain}</span>
                      <button
                        onClick={() => removeDomain(domain)}
                        className="remove-domain"
                        aria-label={`Remove ${domain}`}
                      >
                        <Close size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {domainWhitelist.length === 0 && (
              <div className="empty-state">
                <span className="material-symbols-outlined">domain_verification</span>
                <p>No domains whitelisted. Add domains to restrict signups.</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="section">
        <h3>Access Control</h3>

        <div className="access-info">
          <div className="info-item">
            <div className="info-icon">
              <span className="material-symbols-outlined">group</span>
            </div>
            <div className="info-content">
              <h4>Workspace Members</h4>
              <p>Users who are members of your workspace have full access to all features and boards.</p>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon">
              <span className="material-symbols-outlined">person</span>
            </div>
            <div className="info-content">
              <h4>Board Guests</h4>
              <p>Users who are invited to specific boards have limited access based on their permissions.</p>
            </div>
          </div>

          <div className="info-item">
            <div className="info-icon">
              <span className="material-symbols-outlined">public</span>
            </div>
            <div className="info-content">
              <h4>Public Signup Users</h4>
              <p>When public signup is enabled, new users are automatically added as workspace members with limited access.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="section">
        <h3>Usage & Limits</h3>

        <div className="usage-stats">
          <div className="stat-item">
            <div className="stat-value">
              {config?.monthlyActiveUsers || 0}
            </div>
            <div className="stat-label">Monthly Active Users</div>
          </div>

          <div className="stat-item">
            <div className="stat-value">
              {Math.round((config?.storageUsed || 0) / 1024 / 1024) || 0} MB
            </div>
            <div className="stat-label">Storage Used</div>
          </div>

          <div className="stat-item">
            <div className="stat-value">
              {config?.apiCallsThisMonth || 0}
            </div>
            <div className="stat-label">API Calls This Month</div>
          </div>
        </div>
      </div>

      <AutoSaveStatus
        autoSaving={autoSaving}
        lastSaved={lastSaved}
      />
    </div>
  );
};

export default FeatureSettings;
